/**
 * Axios Instance Configuration
 * 
 * Centralized API client with interceptors for authentication
 */

import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:1337/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token to requests
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('authToken');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle specific error cases
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      const requestUrl = error.config?.url || '';
      const shouldResetAuthOn401 =
        requestUrl.includes('/auth/me') ||
        requestUrl.includes('/auth/logout');
      
      switch (status) {
        case 401:
          // Only invalidate session for core auth validation endpoints.
          if (shouldResetAuthOn401) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            window.dispatchEvent(new Event('auth:unauthorized'));
          }
          break;
        
        case 403:
          console.error('Access forbidden:', data.message);
          break;
        
        case 404:
          console.error('Resource not found:', data.message);
          break;
        
        case 500:
          console.error('Server error:', data.message);
          break;
        
        default:
          console.error('API error:', data.message || error.message);
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('Network error: No response from server');
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

export default api;
